import { apiClient } from '@/lib/apiClient';
import type { AssessmentResult, ScoreBreakdown } from '@/types/equip360';

const ORG_CONTEXT_KEY = 'pending_assessment_org';

export interface OrganizationContext {
  organizationId: string;
  organizationName: string;
}

export function getOrganizationContext(): OrganizationContext | null {
  try {
    const stored = localStorage.getItem(ORG_CONTEXT_KEY);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error('Failed to parse org context:', e);
  }
  return null;
}

export function setOrganizationContext(context: OrganizationContext): void {
  try {
    localStorage.setItem(ORG_CONTEXT_KEY, JSON.stringify(context));
  } catch (e) {
    console.error('Failed to save org context:', e);
  }
}

export function clearOrganizationContext(): void {
  try {
    localStorage.removeItem(ORG_CONTEXT_KEY);
  } catch (e) {
    console.error('Failed to clear org context:', e);
  }
}

export function arrayToScores(arr: number[]): ScoreBreakdown {
  if (!Array.isArray(arr) || arr.length < 13) {
    arr = Array(13).fill(0);
  }
  const eq = {
    SA: arr[0], SR: arr[1], M: arr[2], E: arr[3], SS: arr[4],
    total: arr[0] + arr[1] + arr[2] + arr[3] + arr[4],
    percentage: 0,
  };
  eq.percentage = Math.round((eq.total / (5 * 80)) * 100);

  const bed = {
    B: arr[5], EX: arr[6], D: arr[7],
    total: arr[5] + arr[6] + arr[7],
    percentage: 0,
  };
  bed.percentage = Math.round((bed.total / (3 * 80)) * 100);

  const culture = {
    T: arr[8], PS: arr[9], CQ: arr[10], TS: arr[11], ER: arr[12],
    total: arr[8] + arr[9] + arr[10] + arr[11] + arr[12],
    percentage: 0,
  };
  culture.percentage = Math.round((culture.total / (5 * 80)) * 100);

  const maxPossible = 13 * 80;
  const total = eq.total + bed.total + culture.total;

  return {
    eq,
    bed,
    culture,
    overall: {
      total,
      percentage: Math.round((total / maxPossible) * 100),
      maxPossible,
    },
  };
}

function scoresToArray(scores: ScoreBreakdown): number[] {
  return [
    scores.eq.SA,
    scores.eq.SR,
    scores.eq.M,
    scores.eq.E,
    scores.eq.SS,
    scores.bed.B,
    scores.bed.EX,
    scores.bed.D,
    scores.culture.T,
    scores.culture.PS,
    scores.culture.CQ,
    scores.culture.TS,
    scores.culture.ER,
  ];
}

export interface SaveAssessmentParams {
  userId: string;
  result: AssessmentResult;
  organizationId?: string;
  responses?: unknown[];
  firstName?: string;
  lastName?: string;
}

export interface SaveAssessmentResult {
  success: boolean;
  assessmentId?: string;
  error?: string;
}

export async function saveAssessment({
  result,
  organizationId,
  responses,
  firstName,
  lastName,
}: SaveAssessmentParams): Promise<SaveAssessmentResult> {
  try {
    const scoresArray = scoresToArray(result.scores);
    const assessment = await apiClient.assessments.save({
      organizationId: organizationId ?? null,
      scores: scoresArray,
      leadershipFamily: result.leadershipFamily,
      leadershipType: result.leadershipType,
      responses: responses ?? null,
      firstName,
      lastName,
    });

    if (organizationId) {
      clearOrganizationContext();
    }

    return { success: true, assessmentId: assessment.id };
  } catch (err) {
    console.error('Error saving assessment:', err);
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

export async function sendAssessmentNotification(data: {
  userName: string;
  userEmail: string;
  leadershipFamily: string;
  leadershipType: string;
  scores: number[];
  organizationId?: string;
  organizationName?: string;
  company?: string;
  jobTitle?: string;
  pdfBase64?: string;
}): Promise<void> {
  try {
    await fetch('/api/assessment-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  } catch (err) {
    console.error('Failed to send assessment notification:', err);
  }
}

export async function getUserAssessments() {
  try {
    return await apiClient.assessments.mine();
  } catch (err) {
    console.error('Failed to fetch assessments:', err);
    return [];
  }
}
